import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { CheckCircle, ArrowSquareOut, DownloadSimple, UserPlus, DeviceMobile, House, ShieldStar, Bank, CreditCard, Gift, LinkSimple, ChatText, EnvelopeSimple, UploadSimple, Clock, PenNib, Question, CurrencyDollar, FilePdf, CalendarPlus } from "@phosphor-icons/react";

function InviteLink() {
  const { data } = useQuery({ queryKey: ["referral-link"], queryFn: () => api.get("/referrals/link").then((r) => r.data) });
  if (!data?.code) return null;
  const url = `${window.location.origin}/refer/${data.code}`;
  const msg = encodeURIComponent(`My tax advisory firm has been fantastic — secure portal, snap documents from your phone, never miss a deadline. Check them out: ${url}`);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch {
      const ta = document.createElement("textarea"); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    toast.success("Invite link copied — text or email it to any business owner");
  };
  return (
    <div className="rounded-md border border-dashed border-zinc-300 bg-zinc-50/60 p-2.5 mb-3" data-testid="invite-link-block">
      <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Your personal invite link</p>
      <p data-testid="invite-link-url" className="text-[11px] font-mono text-zinc-700 truncate mb-1.5">{url}</p>
      <div className="flex gap-2">
        <button data-testid="invite-copy-btn" onClick={copy} className="flex items-center gap-1 text-[10px] text-zinc-700 underline hover:no-underline"><LinkSimple size={11} /> Copy</button>
        <a data-testid="invite-sms-btn" href={`sms:?&body=${msg}`} className="flex items-center gap-1 text-[10px] text-zinc-700 underline hover:no-underline"><ChatText size={11} /> Text it</a>
        <a data-testid="invite-email-btn" href={`mailto:?subject=${encodeURIComponent("A tax advisory firm worth knowing")}&body=${msg}`} className="flex items-center gap-1 text-[10px] text-zinc-700 underline hover:no-underline"><EnvelopeSimple size={11} /> Email it</a>
      </div>
    </div>
  );
}

const money = (n) => `$${(n || 0).toLocaleString()}`;

function DeclineLink({ engagementId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const decline = useMutation({
    mutationFn: () => api.post("/portal/decline", { engagement_id: engagementId, reason }),
    onSuccess: ({ data }) => { toast.success(data.message); setOpen(false); qc.invalidateQueries(); },
    onError: (e) => toast.error(errText(e)),
  });
  if (!open) {
    return <button data-testid={`decline-link-${engagementId}`} onClick={() => setOpen(true)} className="text-[10px] text-zinc-400 underline hover:text-zinc-600 mt-1.5">No longer filing with us?</button>;
  }
  return (
    <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-2.5" data-testid={`decline-form-${engagementId}`}>
      <p className="text-[11px] text-zinc-600 mb-1.5">Let us know why (optional) — we'll close this return out properly and stop the reminders.</p>
      <Textarea data-testid="decline-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. filing on my own this year" className="text-xs min-h-[50px] mb-1.5" />
      <div className="flex gap-2">
        <Button data-testid="decline-confirm-btn" size="sm" variant="destructive" className="h-7 text-[11px]" disabled={decline.isPending} onClick={() => decline.mutate()}>Confirm — not filing this return</Button>
        <Button data-testid="decline-cancel-btn" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setOpen(false)}>Never mind</Button>
      </div>
    </div>
  );
}

const MILESTONE_NOTES = [
  { title: "Intake / Data Collection", detail: "You're uploading documents from your personalized checklist. Once complete, we move to preparation." },
  { title: "Preparation", detail: "Your preparer is entering figures, tying them to source docs, and building schedules." },
  { title: "CPA Technical Review", detail: "A senior CPA is reviewing every line, cross-checking tax positions, and flagging optimizations." },
  { title: "Ready for Signature", detail: "The return is finalized. You'll get an e-signature request for Form 8879 to authorize e-file." },
  { title: "Transmitted to IRS/State", detail: "Your return is filed with all taxing authorities. Confirmation typically arrives within 24–48 hours." },
  { title: "Accepted", detail: "Every taxing authority has accepted your return. You'll find the acceptance receipts in your vault." },
];

export function ReturnTracker({ tracker, refundLinks }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 hover:shadow-md transition-shadow duration-200" data-testid="return-tracker">
      <h3 className="text-sm font-medium text-slate-900 mb-4">Your tax return tracker</h3>
      {(tracker || []).map((t) => (
        <div key={t.engagement_id} className="mb-5 last:mb-0" data-testid={`tracker-${t.engagement_id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-zinc-700">{t.service_type} · {t.tax_year}</p>
            <p className="text-[10px] text-zinc-400">Target completion: <span className="font-mono">{t.est_completion || "TBD"}</span></p>
          </div>
          <div className="flex items-center">
            {t.milestones.map((m, i) => {
              const notes = MILESTONE_NOTES[i] || { title: m, detail: "Details will be shared when this stage begins." };
              const isCurrent = i === t.milestone;
              const isDone = i <= t.milestone;
              return (
                <div key={m} className="flex-1 flex flex-col items-center relative">
                  {i > 0 && <div className={`absolute left-0 right-1/2 top-[7px] h-0.5 ${i <= t.milestone ? "bg-emerald-500" : "bg-zinc-200"}`} />}
                  {i < t.milestones.length - 1 && <div className={`absolute left-1/2 right-0 top-[7px] h-0.5 ${i < t.milestone ? "bg-emerald-500" : "bg-zinc-200"}`} />}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid={`tracker-stage-${t.engagement_id}-${i}`}
                        aria-label={`Stage: ${m}`}
                        className={`w-3.5 h-3.5 rounded-full z-10 border-2 cursor-pointer transition-transform duration-150 hover:scale-125 ${isDone ? (isCurrent ? "bg-emerald-500 border-emerald-500 ring-4 ring-emerald-500/20" : "bg-emerald-500 border-emerald-500") : "bg-white border-zinc-300"}`} />
                    </PopoverTrigger>
                    <PopoverContent side="top" className="w-64 p-3" data-testid={`tracker-stage-popover-${t.engagement_id}-${i}`}>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Stage {i + 1} of {t.milestones.length}</p>
                      <p className="text-sm font-semibold text-slate-900 mb-1">{notes.title}</p>
                      <p className="text-xs text-zinc-600 leading-relaxed">{notes.detail}</p>
                      {isCurrent && t.est_completion && (
                        <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center gap-1.5 text-[11px] text-emerald-700">
                          <Clock size={11} weight="fill" /> Estimated completion: <span className="font-mono">{t.est_completion}</span>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <p className={`text-[9px] mt-1.5 text-center leading-tight px-0.5 ${isCurrent ? "text-slate-900 font-semibold" : isDone ? "text-emerald-700" : "text-zinc-400"}`}>{m}</p>
                </div>
              );
            })}
          </div>
          {t.missing_count > 0 && <p className="text-[11px] text-amber-700 mt-2">Upload your {t.missing_count} remaining document{t.missing_count > 1 ? "s" : ""} to stay on track.</p>}
          {t.variance_explanation && (
            <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="variance-box">
              <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Why your numbers changed vs last year</p>
              <p className="text-xs text-zinc-700">{t.variance_explanation}</p>
            </div>
          )}
          <DeclineLink engagementId={t.engagement_id} />
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <a data-testid="irs-refund-link" href={refundLinks?.irs} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-zinc-600 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 hover:shadow-sm transition-all duration-150">
          <ArrowSquareOut size={13} /> IRS "Where's My Refund?"
        </a>
        <a data-testid="state-refund-link" href={refundLinks?.state} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-zinc-600 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 hover:shadow-sm transition-all duration-150">
          <ArrowSquareOut size={13} /> State comptroller status
        </a>
      </div>
    </div>
  );
}

export function ConfirmationsVault({ confirmations }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 hover:shadow-md transition-shadow duration-200" data-testid="confirmations-vault">
      <h3 className="text-sm font-medium text-slate-900 mb-3">Filing confirmations vault</h3>
      {(!confirmations?.length) ? (
        <div className="rounded-md border border-dashed border-zinc-200 bg-zinc-50/60 px-3 py-4 text-center" data-testid="confirmations-empty">
          <ShieldStar size={20} className="text-slate-400 mx-auto mb-1" />
          <p className="text-xs text-slate-700 font-medium">No filings archived yet</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">IRS &amp; state acceptance receipts land here the moment your return is accepted.</p>
        </div>
      ) : (
        confirmations.map((c) => (
          <div key={c.engagement_id} className="flex items-center justify-between py-1.5 border-b border-zinc-100 last:border-0">
            <span className="text-xs text-zinc-700 flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> {c.label}</span>
            <button className="text-[10px] text-zinc-500 flex items-center gap-1 hover:text-slate-900" onClick={() => toast.info("Receipt download will be available once return archives sync")}><DownloadSimple size={12} /> Receipt</button>
          </div>
        ))
      )}
    </div>
  );
}

const TODO_ICONS = { upload: UploadSimple, sign: PenNib, pay: CurrencyDollar, question: Question };
const TODO_ACTIONS = { upload: "Upload now", sign: "Sign", pay: "Pay", question: "Answer" };

export function TodoPanel({ todos, onAction }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-5 hover:shadow-md transition-shadow duration-200" data-testid="todo-panel">
      <h3 className="text-sm font-medium text-slate-900 mb-3">Your CPA to-do list ({todos?.length || 0})</h3>
      <div className="space-y-1.5">
        {(todos || []).map((t, i) => {
          const Icon = TODO_ICONS[t.kind] || Question;
          const actionLabel = TODO_ACTIONS[t.kind] || "Open";
          return (
            <div key={i} className="flex items-center gap-2 text-xs text-zinc-700 bg-white border border-zinc-200 hover:border-slate-900 hover:shadow-sm rounded-md px-2.5 py-2 transition-all duration-150 group" data-testid={`todo-item-${t.kind}-${i}`}>
              <Icon size={13} weight="fill" className={`shrink-0 ${t.kind === "sign" ? "text-amber-600" : t.kind === "pay" ? "text-emerald-600" : t.kind === "upload" ? "text-slate-500" : "text-indigo-500"}`} />
              <span className="flex-1 truncate">{t.label}</span>
              {onAction && (
                <button
                  data-testid={`todo-action-${t.kind}-${i}`}
                  onClick={() => onAction(t)}
                  className="opacity-100 md:opacity-0 md:group-hover:opacity-100 flex items-center gap-1 text-[10px] font-medium text-slate-900 hover:text-emerald-700 transition-colors duration-150 shrink-0"
                  title={actionLabel}>
                  <Icon size={11} /> {actionLabel}
                </button>
              )}
            </div>
          );
        })}
        {!todos?.length && (
          <div className="rounded-md border border-dashed border-emerald-200 bg-white/70 px-3 py-4 text-center" data-testid="todo-empty">
            <CheckCircle size={20} weight="fill" className="text-emerald-500 mx-auto mb-1" />
            <p className="text-xs text-slate-700 font-medium">You're all caught up.</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">We'll ping you here the moment your CPA needs anything.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function BillingCenter({ invoices }) {
  const qc = useQueryClient();
  const { data: credit } = useQuery({ queryKey: ["referral-credit"], queryFn: () => api.get("/referrals/credit").then((r) => r.data) });
  const applyCredit = useMutation({
    mutationFn: (id) => api.post(`/invoices/${id}/apply-credit`),
    onSuccess: ({ data }) => { toast.success(data.message); qc.invalidateQueries({ queryKey: ["referral-credit"] }); qc.invalidateQueries({ queryKey: ["portal-extras"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const pay = useMutation({
    mutationFn: ({ id, method }) => api.post(`/portal/invoices/${id}/pay`, { method }),
    onSuccess: () => { toast.success("Payment received — thank you!"); qc.invalidateQueries({ queryKey: ["portal-extras"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const installments = useMutation({
    mutationFn: (id) => api.post(`/portal/invoices/${id}/installments`),
    onSuccess: () => toast.success("Installment plan requested — the firm will follow up"),
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="billing-center">
      <h3 className="text-sm font-medium text-zinc-900 mb-3">Billing center</h3>
      {credit?.available > 0 && (
        <p data-testid="referral-credit-banner" className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mb-3">
          You have <span className="font-medium">{money(credit.available)}</span> in referral credit — apply it to any open invoice below.
        </p>
      )}
      <div className="space-y-2">
        {(invoices || []).map((i) => (
          <div key={i.id} className="rounded-md border border-zinc-200 p-3" data-testid={`invoice-${i.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-zinc-900 font-mono">
                  {i.number} · {money(i.amount)}
                  {i.credit_applied > 0 && <span className="text-emerald-700"> − {money(i.credit_applied)} credit = {money(Math.max(i.amount - i.credit_applied, 0))}</span>}
                </p>
                <p className="text-[11px] text-zinc-500">{i.description} · {i.fee_type}</p>
              </div>
              {i.status === "paid" ? (
                <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-1">PAID {i.method === "referral_credit" ? "WITH CREDIT" : i.method?.toUpperCase()}</span>
              ) : (
                <div className="flex gap-1.5">
                  {credit?.available > 0 && (
                    <Button data-testid={`apply-credit-btn-${i.id}`} size="sm" variant="outline" className="h-7 text-[11px] text-emerald-700 border-emerald-300" disabled={applyCredit.isPending} onClick={() => applyCredit.mutate(i.id)}>
                      <Gift size={12} className="mr-1" /> Apply credit
                    </Button>
                  )}
                  <Button data-testid={`pay-ach-btn-${i.id}`} size="sm" variant="outline" className="h-7 text-[11px]" disabled={pay.isPending} onClick={() => pay.mutate({ id: i.id, method: "ach" })}><Bank size={12} className="mr-1" /> ACH</Button>
                  <Button data-testid={`pay-card-btn-${i.id}`} size="sm" className="h-7 text-[11px]" disabled={pay.isPending} onClick={() => pay.mutate({ id: i.id, method: "card" })}><CreditCard size={12} className="mr-1" /> Card</Button>
                </div>
              )}
            </div>
            {i.status !== "paid" && i.amount >= 2000 && (
              <button data-testid={`installments-btn-${i.id}`} className="text-[10px] text-zinc-500 mt-1.5 hover:text-zinc-900" disabled={installments.isPending} onClick={() => installments.mutate(i.id)}>
                Set up an installment plan →
              </button>
            )}
          </div>
        ))}
        {!invoices?.length && (
          <div className="rounded-md border border-dashed border-emerald-200 bg-emerald-50/40 px-3 py-4 text-center" data-testid="invoices-empty">
            <CheckCircle size={20} weight="fill" className="text-emerald-500 mx-auto mb-1" />
            <p className="text-xs text-slate-700 font-medium">No open invoices</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Your account is fully paid up. New engagements will show here.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdvisorySection({ advisory }) {
  if (!advisory) return null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="advisory-section">
      <h3 className="text-sm font-medium text-zinc-900 mb-3">Advisory concierge</h3>
      <div className="space-y-2.5">
        <div className="flex items-start gap-2.5">
          <ShieldStar size={16} className="text-zinc-500 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-zinc-800">{advisory.insurance?.policy}</p>
            <p className="text-[11px] text-zinc-500">Coverage {advisory.insurance?.coverage} · renews <span className="font-mono">{advisory.insurance?.renewal}</span></p>
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <Bank size={16} className="text-zinc-500 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-zinc-800">Mortgage — {advisory.mortgage?.property}</p>
            <p className="text-[11px] text-zinc-500">{advisory.mortgage?.status}: {advisory.mortgage?.stage}</p>
          </div>
        </div>
        {(advisory.real_estate || []).map((r) => (
          <div key={r.property} className="flex items-start gap-2.5">
            <House size={16} className="text-zinc-500 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-zinc-800">{r.property} · <span className="font-mono">{r.cost_basis}</span></p>
              <p className="text-[11px] text-zinc-500">{r.note}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReferralWidget({ referrals }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const refer = useMutation({
    mutationFn: () => api.post("/referrals", { referred_name: name, referred_email: email }),
    onSuccess: (r) => { toast.success(r.data?.invite_email_sent ? "Referral sent — the invite email is on its way!" : "Referral saved — you'll earn $50 when they engage!"); setName(""); setEmail(""); qc.invalidateQueries({ queryKey: ["portal-extras"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const testEmail = useMutation({
    mutationFn: () => api.post("/referrals/test"),
    onSuccess: (r) => toast.success(r.data?.sent ? `Preview sent to ${r.data.to} — check your inbox` : "Preview couldn't be sent right now"),
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="referral-widget">
      <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><UserPlus size={15} /> Refer a friend, earn $50</h3>
      <p className="text-[11px] text-zinc-500 mb-2">Earned: <span className="font-mono text-emerald-700">{money(referrals?.bonus_earned)}</span> · Pending: <span className="font-mono">{money(referrals?.bonus_pending)}</span></p>
      <Link data-testid="view-rewards-link" to="/portal/rewards" className="inline-block text-[11px] text-indigo-600 underline hover:no-underline mb-3">View your rewards tracker →</Link>
      <InviteLink />
      <div className="space-y-2 mb-3">
        <Input data-testid="referral-name-input" placeholder="Friend's name" value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-xs" />
        <Input data-testid="referral-email-input" placeholder="Friend's email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-8 text-xs" />
        <Button data-testid="referral-submit-btn" size="sm" className="w-full h-8" disabled={!name || !email || refer.isPending} onClick={() => refer.mutate()}>Send referral</Button>
        <button data-testid="referral-test-btn" disabled={testEmail.isPending} onClick={() => testEmail.mutate()} className="w-full text-[11px] text-zinc-500 underline hover:text-zinc-800 transition-colors duration-150">
          {testEmail.isPending ? "Sending preview…" : "Send a test invite to myself first"}
        </button>
      </div>
      <div className="space-y-1">
        {(referrals?.recent || []).map((r) => (
          <div key={r.id} className="flex items-center justify-between text-[11px] py-1 border-b border-zinc-100 last:border-0">
            <span className="text-zinc-700">{r.referred_name}</span>
            <span className="flex items-center gap-1.5">
              <span className="text-[9px] uppercase bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">{r.status}</span>
              <span className={`font-mono ${r.bonus_status === "paid" ? "text-emerald-700" : "text-zinc-400"}`}>${r.bonus_amount}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SecurityPane({ deviceLog }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="security-pane">
      <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><DeviceMobile size={15} /> Device & access history</h3>
      <p className="text-[11px] text-zinc-500 mb-3">Every successful sign-in to your tax data. MFA is enforced on your account.</p>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {(deviceLog || []).map((e) => (
          <div key={e.id} className="text-[11px] text-zinc-600 py-1 border-b border-zinc-100 last:border-0">
            <span className="font-mono text-[9px] text-zinc-400 mr-2">{new Date(e.created_at).toLocaleString()}</span>
            {e.details}
          </div>
        ))}
        {!deviceLog?.length && <p className="text-xs text-zinc-400 italic">No sign-ins recorded yet.</p>}
      </div>
    </div>
  );
}
